// Building management → Budget & charges (the demo's bmMoney): this year's service-charge budget and the figures the
// service-charge year runs on — year end, apportionment, demands, ground rent, the reserve fund, contracts committed
// against the budget. Editing goes through the same budget editor as the Accounts tab and Building → Budgets.
import { Can } from '../Can';
import { Badge, Button, Card } from '../ui';
import { StatTile } from '../charts/StatTile';
import { apportionLabel, demandLabel } from '../../lib/fees';
import { formatDate, formatMoney } from '../../lib/viz';
import type { BuildingSummary } from '../../types/api';

type Money = NonNullable<BuildingSummary['money']>;

function Row({ label, children, sub }: { label: string; children: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-[12.5px] text-ink-500">{label}</span>
      <span className="text-right text-[13px] font-medium text-ink-900">{children}{sub && <span className="block text-[11.5px] font-normal text-ink-400">{sub}</span>}</span>
    </div>
  );
}

export function BudgetChargesPanel({ money, units, onEdit }: { money: Money; /** "Units" on the development overview (hero facts). */ units: string | null; onEdit: () => void }) {
  const m = (v: number | null | undefined) => (v === null || v === undefined ? '—' : formatMoney(v, money.currency));
  const has = money.budgetMinor !== null;
  const usedPct = has && money.budgetMinor ? Math.round((money.committedContractsMinor / money.budgetMinor) * 100) : null;
  const unitCount = money.unitCount || Number(units) || 0;
  return (
    <div className="space-y-2.5">
      <div className="tile-row">
        <StatTile compact label={`Budget ${money.year}`} value={m(money.budgetMinor)} hint={has ? (money.status === 'APPROVED' ? 'Approved' : 'Draft') : 'Not set yet'} status={has ? undefined : 'warning'} />
        <StatTile compact label="Average per unit" value={m(money.perUnitMinor)} hint={unitCount ? `${unitCount} units` : undefined} />
        <StatTile compact label="Reserve fund" value={m(money.reserveBalanceMinor)} hint={money.reserveContributionMinor !== null ? `+ ${m(money.reserveContributionMinor)} a year` : undefined} />
        <StatTile compact label="Under contract" value={m(money.committedContractsMinor)} hint="Service-charge contracts pa" />
        <StatTile compact label="Year end" value={money.yearEnd ? formatDate(money.yearEnd) : '—'} />
      </div>

      <div className="grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card title={`Service-charge budget ${money.year}`} subtitle={has ? undefined : 'No budget for this year yet'}
          actions={<Can do="budgets.read"><Button size="sm" variant="secondary" onClick={onEdit}>{has ? 'Edit' : 'Set budget'}</Button></Can>}>
          <div className="-my-2.5 divide-y divide-ink-100">
            <Row label="Status">{money.status ? <Badge value={money.status === 'APPROVED' ? 'Approved' : 'Draft'} tone={money.status === 'APPROVED' ? 'good' : 'warning'} /> : '—'}</Row>
            <Row label="Budget for the year">{m(money.budgetMinor)}</Row>
            <Row label="Spent so far" sub="From the ledger, this calendar year">{m(money.actualMinor)}</Row>
            <Row label="Year end">{money.yearEnd ? formatDate(money.yearEnd) : '—'}</Row>
            <Row label="Apportionment">{apportionLabel(money.apportionment) ?? '—'}</Row>
            <Row label="Demands" sub={money.demandsRaised !== null ? `${money.demandsRaised} raised this year` : undefined}>
              {demandLabel(money.demandFrequency) ?? '—'}{money.nextDemand ? ` · next ${formatDate(money.nextDemand)}` : ''}
            </Row>
          </div>
        </Card>
        <Card title="Per unit and reserves">
          <div className="-my-2.5 divide-y divide-ink-100">
            <Row label="Units in the building">{unitCount || '—'}</Row>
            <Row label="Average per unit">{m(money.perUnitMinor)}</Row>
            <Row label="Ground rent per unit">{money.groundRentPerUnitMinor !== null ? `${m(money.groundRentPerUnitMinor)} a year` : '—'}</Row>
            <Row label="Reserve fund balance">{m(money.reserveBalanceMinor)}</Row>
            <Row label="Reserve contribution">{money.reserveContributionMinor !== null ? `${m(money.reserveContributionMinor)} a year` : '—'}</Row>
            <Row label="Contracts committed vs budget" sub={usedPct !== null ? `${usedPct}% of the budget is under contract` : undefined}>
              {m(money.committedContractsMinor)} <span className="font-normal text-ink-400">of {m(money.budgetMinor)}</span>
            </Row>
          </div>
        </Card>
      </div>

      <p className="rounded-xl border border-ink-100 bg-ink-50 px-4 py-3 text-[12.5px] leading-relaxed text-ink-600">
        Service-charge schedules and apportionment per schedule are not configured yet — needs the client's apportionment method.
        Until then the figures here are for the whole building, and the average per unit is the budget divided equally.
      </p>
    </div>
  );
}
